import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useAppContext } from '../AppContext';
import './OnboardingFlow.css';

const OnboardingFlow = ({ onComplete }) => {
    const { t } = useTranslation();
    const { updateUserProfile, currentUserProfile } = useAppContext();
    const [step, setStep] = useState(1);
    const [sliderValue, setSliderValue] = useState(5);
    const [selectedGoal, setSelectedGoal] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedNode, setSelectedNode] = useState('');
    const [syncProgress, setSyncProgress] = useState(0);
    const [syncText, setSyncText] = useState('');

    // Simulated Sync text updates
    useEffect(() => {
        if (step === 5) {
            const interval = setInterval(() => {
                setSyncProgress((prev) => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        setTimeout(() => {
                            setStep(6); // Move to paywall
                        }, 800);
                        return 100;
                    }
                    return prev + 2;
                });
            }, 60);

            return () => clearInterval(interval);
        }
    }, [step]);

    useEffect(() => {
        if (syncProgress < 25) {
            setSyncText('Aligning cognitive frequency...');
        } else if (syncProgress < 50) {
            setSyncText('Mapping core Aura color...');
        } else if (syncProgress < 75) {
            setSyncText('Synchronizing with the Nexus...');
        } else {
            setSyncText('Awakening autonomous Echoes...');
        }
    }, [syncProgress]);

    const handleNext = () => {
        setStep(step + 1);
    };

    const handlePrev = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleFinishOnboarding = async (subscribed = false) => {
        try {
            // Determine Aura color based on onboarding responses
            let auraColor = 'indigo';
            if (sliderValue > 7) auraColor = 'red';
            else if (selectedGoal === 'Solitude') auraColor = 'blue';
            else if (selectedGoal === 'Peace') auraColor = 'green';
            else if (selectedGoal === 'Creative Expression') auraColor = 'purple';
            else if (selectedTime === 'Late night thoughts') auraColor = 'amber';

            const updates = {
                hasCompletedOnboarding: true,
                auraColor: auraColor,
                onboardingAnswers: {
                    mindNoise: sliderValue,
                    goal: selectedGoal,
                    timeOfDay: selectedTime,
                    nodeType: selectedNode
                },
                isPro: subscribed || (currentUserProfile?.isPro || false)
            };

            await updateUserProfile(currentUserProfile.id, updates);
            if (onComplete) onComplete();
        } catch (error) {
            console.error("Failed to complete onboarding:", error);
        }
    };

    return (
        <div className="onboarding-overlay flex justify-center items-center p-4">
            <div className="onboarding-card bg-gray-950/90 backdrop-blur-xl p-8 rounded-3xl border border-indigo-500/30 max-w-lg w-full text-white shadow-[0_0_50px_rgba(99,102,241,0.2)] flex flex-col justify-between relative overflow-hidden">
                
                {/* Decorative glowing gradient elements */}
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

                {/* Header (Steps indicator) */}
                {step <= 5 && (
                    <div className="flex justify-between items-center mb-6 z-10">
                        <span className="text-xs text-indigo-400 font-semibold tracking-widest uppercase">
                            Calibration Step {step} of 4
                        </span>
                        <div className="flex gap-1.5">
                            {[1, 2, 3, 4].map((i) => (
                                <div 
                                    key={i} 
                                    className={`w-8 h-1 rounded-full transition-all duration-300 ${
                                        i === step ? 'bg-indigo-400' : i < step ? 'bg-indigo-600' : 'bg-gray-800'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="flex-grow flex flex-col justify-center min-h-[300px] z-10">
                    
                    {/* STEP 1: Slide Noise Check */}
                    {step === 1 && (
                        <div className="animate-fadeIn">
                            <h2 className="text-2xl font-bold font-playfair mb-2 text-indigo-100">
                                How noisy does your mind feel today?
                            </h2>
                            <p className="text-gray-400 text-sm mb-12">
                                Adjust the slider to set your current cognitive baseline.
                            </p>
                            
                            <div className="relative mt-8 mb-8 px-4">
                                <input 
                                    type="range" 
                                    min="1" 
                                    max="10" 
                                    value={sliderValue} 
                                    onChange={(e) => setSliderValue(parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-4 font-semibold">
                                    <span>Quiet Sanctuary</span>
                                    <span>Chaotic Storm</span>
                                </div>
                            </div>

                            <div className="text-center mt-6">
                                <span className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 animate-pulse">
                                    Level {sliderValue}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Goal Selection */}
                    {step === 2 && (
                        <div className="animate-fadeIn">
                            <h2 className="text-2xl font-bold font-playfair mb-2 text-indigo-100">
                                What are you seeking most right now?
                            </h2>
                            <p className="text-gray-400 text-sm mb-8">
                                Helps customize the Echo suggestions and Nexus recommendations.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-4">
                                {['Connection', 'Solitude', 'Peace', 'Creative Expression'].map((goal) => (
                                    <button
                                        key={goal}
                                        onClick={() => setSelectedGoal(goal)}
                                        className={`p-4 rounded-2xl border text-center transition-all duration-300 font-semibold ${
                                            selectedGoal === goal 
                                            ? 'border-indigo-400 bg-indigo-500/20 text-indigo-200 shadow-[0_0_15px_rgba(99,102,241,0.25)]' 
                                            : 'border-gray-800 bg-gray-900/40 text-gray-400 hover:border-gray-700 hover:text-white'
                                        }`}
                                    >
                                        {goal}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 3: Time Preference */}
                    {step === 3 && (
                        <div className="animate-fadeIn">
                            <h2 className="text-2xl font-bold font-playfair mb-2 text-indigo-100">
                                When does the world feel most overwhelming?
                            </h2>
                            <p className="text-gray-400 text-sm mb-8">
                                We will send gentle reminders and calming visual anchors during these times.
                            </p>
                            
                            <div className="space-y-3">
                                {['Morning rush', 'Mid-day grind', 'Late night thoughts', 'Always in harmony'].map((time) => (
                                    <button
                                        key={time}
                                        onClick={() => setSelectedTime(time)}
                                        className={`w-full p-4 rounded-xl border text-left transition-all duration-300 flex items-center justify-between font-semibold ${
                                            selectedTime === time 
                                            ? 'border-indigo-400 bg-indigo-500/20 text-indigo-200 shadow-[0_0_15px_rgba(99,102,241,0.25)]' 
                                            : 'border-gray-800 bg-gray-900/40 text-gray-400 hover:border-gray-700 hover:text-white'
                                        }`}
                                    >
                                        <span>{time}</span>
                                        {selectedTime === time && <LucideIcons.Check size={18} className="text-indigo-400" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 4: Constellation Node selection */}
                    {step === 4 && (
                        <div className="animate-fadeIn">
                            <h2 className="text-2xl font-bold font-playfair mb-2 text-indigo-100">
                                Choose your cosmic star profile
                            </h2>
                            <p className="text-gray-400 text-sm mb-6">
                                This determines the visual appearance of your primary star in the 3D constellation.
                            </p>
                            
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { name: 'Pulsar Core', desc: 'Dynamic & bright', color: 'text-cyan-400', glow: 'shadow-cyan-500/50' },
                                    { name: 'Nebula Orb', desc: 'Mystical & soft', color: 'text-indigo-400', glow: 'shadow-indigo-500/50' },
                                    { name: 'Supernova', desc: 'Vibrant & explosive', color: 'text-purple-400', glow: 'shadow-purple-500/50' }
                                ].map((node) => (
                                    <button
                                        key={node.name}
                                        onClick={() => setSelectedNode(node.name)}
                                        className={`p-4 rounded-xl border text-center transition-all duration-300 flex flex-col items-center justify-center gap-2 ${
                                            selectedNode === node.name 
                                            ? 'border-indigo-400 bg-indigo-500/20 shadow-[0_0_20px_rgba(99,102,241,0.3)]' 
                                            : 'border-gray-800 bg-gray-900/40 text-gray-400 hover:border-gray-700'
                                        }`}
                                    >
                                        <div className={`w-8 h-8 rounded-full bg-white/10 flex items-center justify-center ${node.color}`}>
                                            <LucideIcons.Sparkles size={16} className="animate-pulse" />
                                        </div>
                                        <span className="text-sm font-bold text-white">{node.name}</span>
                                        <span className="text-[10px] text-gray-500">{node.desc}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 5: Calibration Simulation */}
                    {step === 5 && (
                        <div className="flex flex-col items-center justify-center text-center py-8 animate-fadeIn">
                            <div className="onboarding-scanner relative w-32 h-32 rounded-full border-2 border-indigo-500/30 flex items-center justify-center mb-6">
                                <div className="absolute inset-2 rounded-full border-2 border-indigo-400/50 animate-ping"></div>
                                <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 opacity-20 animate-pulse"></div>
                                <LucideIcons.Compass size={48} className="text-indigo-400 animate-spin" style={{ animationDuration: '6s' }} />
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-indigo-100">{syncText}</h3>
                            <p className="text-indigo-400 text-sm font-semibold tracking-wider">{syncProgress}% COMPLETE</p>
                            <div className="w-48 h-1 bg-gray-800 rounded-full mt-4 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-100" style={{ width: `${syncProgress}%` }}></div>
                            </div>
                        </div>
                    )}

                    {/* STEP 6: Hard Paywall Gate */}
                    {step === 6 && (
                        <div className="animate-fadeIn relative">
                            <div className="flex justify-center mb-4">
                                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-300">
                                    <LucideIcons.Crown size={36} className="animate-bounce" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold font-playfair mb-1 text-center text-indigo-100">
                                Unlock Harmony Pro
                            </h2>
                            <p className="text-gray-400 text-sm text-center mb-6">
                                Enter the full digital sanctuary. Start your 3-day free trial.
                            </p>

                            <ul className="space-y-3 mb-6 text-sm text-gray-200">
                                <li className="flex items-center"><LucideIcons.CheckCircle2 size={16} className="text-green-400 mr-3 flex-shrink-0" />
                                    <span>Interactive 3D Constellations & Custom Star Profiles</span>
                                </li>
                                <li className="flex items-center"><LucideIcons.CheckCircle2 size={16} className="text-green-400 mr-3 flex-shrink-0" />
                                    <span>Unlimited Aura Chamber diagnostics & Mood Insights</span>
                                </li>
                                <li className="flex items-center"><LucideIcons.CheckCircle2 size={16} className="text-green-400 mr-3 flex-shrink-0" />
                                    <span>Daily interaction with autonomous Echo personas</span>
                                </li>
                                <li className="flex items-center"><LucideIcons.CheckCircle2 size={16} className="text-green-400 mr-3 flex-shrink-0" />
                                    <span>Harmony Pro badge and special comments visual effects</span>
                                </li>
                            </ul>

                            <div className="bg-indigo-950/20 border border-indigo-500/20 p-4 rounded-2xl mb-6 text-center">
                                <p className="text-xs text-indigo-300 uppercase tracking-widest font-semibold mb-1">Onboarding Special Offer</p>
                                <p className="text-lg font-bold text-white">3 Days Free, then $9.99/month</p>
                                <p className="text-[10px] text-gray-400 mt-1">Cancel anytime in your profile settings. No obligation.</p>
                            </div>

                            <div className="space-y-3">
                                <button 
                                    onClick={() => handleFinishOnboarding(true)}
                                    className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold rounded-xl transition duration-300 shadow-[0_0_20px_rgba(99,102,241,0.4)] flex items-center justify-center gap-2"
                                >
                                    <span>Start My Free Trial</span>
                                    <LucideIcons.ArrowRight size={16} />
                                </button>
                                <button 
                                    onClick={() => handleFinishOnboarding(false)}
                                    className="w-full py-3 bg-transparent hover:bg-white/5 text-gray-400 hover:text-white transition duration-300 text-sm font-semibold rounded-xl"
                                >
                                    Proceed with Free Basic Version
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                {step <= 4 && (
                    <div className="flex gap-4 mt-8 pt-4 border-t border-gray-900 z-10">
                        {step > 1 && (
                            <button 
                                onClick={handlePrev}
                                className="flex-1 py-3 bg-gray-900/60 hover:bg-gray-800/80 border border-gray-800 rounded-xl transition font-semibold text-gray-300 flex items-center justify-center gap-2"
                            >
                                <LucideIcons.ArrowLeft size={16} />
                                <span>Back</span>
                            </button>
                        )}
                        <button 
                            onClick={handleNext}
                            disabled={
                                (step === 2 && !selectedGoal) || 
                                (step === 3 && !selectedTime) || 
                                (step === 4 && !selectedNode)
                            }
                            className={`flex-1 py-3 font-semibold rounded-xl transition flex items-center justify-center gap-2 ${
                                ((step === 2 && !selectedGoal) || 
                                (step === 3 && !selectedTime) || 
                                (step === 4 && !selectedNode))
                                ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                            }`}
                        >
                            <span>Continue</span>
                            <LucideIcons.ArrowRight size={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingFlow;
